import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
    Mic,
    MicOff,
    Square,
    Loader2,
    Sparkles,
    Activity,
    Heart,
    AlertTriangle,
    Clock,
    Trash2,
    ChevronRight,
    Play,
    Pause,
    Save,
    RefreshCw,
    Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceJournal() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    
    // Processing state
    const [transcribing, setTranscribing] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Current entry state
    const [transcription, setTranscription] = useState('');
    const [analysis, setAnalysis] = useState(null);
    const [editedTranscription, setEditedTranscription] = useState('');

    const fetchEntries = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/voice-journal?limit=20`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setEntries(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch entries:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    // Recording functions
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'audio/webm'
            });
            
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };
            
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                stream.getTracks().forEach(track => track.stop());
            };
            
            mediaRecorderRef.current.start(1000); // Collect data every second
            setIsRecording(true);
            setRecordingTime(0);
            
            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            
        } catch (error) {
            console.error('Failed to start recording:', error);
            toast.error('Could not access microphone. Please allow microphone access.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const transcribeAudio = async () => {
        if (!audioBlob) return;
        
        setTranscribing(true);
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const res = await fetch(`${API_URL}/api/voice-journal/transcribe`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (res.ok) {
                const data = await res.json();
                setTranscription(data.transcription);
                setEditedTranscription(data.transcription);
                toast.success('Transcription complete!');
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Transcription failed');
            }
        } catch (error) {
            toast.error('Transcription failed');
        } finally {
            setTranscribing(false);
        }
    };

    const analyzeTranscription = async () => {
        if (!editedTranscription) return;
        
        setAnalyzing(true);
        try {
            const res = await fetch(`${API_URL}/api/voice-journal/analyze`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ transcription: editedTranscription })
            });
            
            if (res.ok) {
                const data = await res.json();
                setAnalysis(data);
                toast.success('Analysis complete!');
            } else {
                toast.error('Analysis failed');
            }
        } catch (error) {
            toast.error('Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    };

    const saveEntry = async () => {
        if (!editedTranscription) return;
        
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/voice-journal`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    transcription: editedTranscription,
                    symptoms: analysis?.symptoms || [],
                    feelings: analysis?.feelings || [],
                    triggers: analysis?.triggers || [],
                    severity: analysis?.severity,
                    summary: analysis?.summary || '',
                    suggested_trackers: analysis?.suggested_trackers || [],
                    audio_duration: recordingTime
                })
            });
            
            if (res.ok) {
                toast.success('Journal entry saved!');
                // Reset state
                setAudioBlob(null);
                setTranscription('');
                setEditedTranscription('');
                setAnalysis(null);
                setRecordingTime(0);
                fetchEntries();
            } else {
                toast.error('Failed to save entry');
            }
        } catch (error) {
            toast.error('Failed to save entry');
        } finally {
            setSaving(false);
        }
    };

    const deleteEntry = async (entryId) => {
        try {
            const res = await fetch(`${API_URL}/api/voice-journal/${entryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                toast.success('Entry deleted');
                fetchEntries();
            }
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const resetRecording = () => {
        setAudioBlob(null);
        setTranscription('');
        setEditedTranscription('');
        setAnalysis(null);
        setRecordingTime(0);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const navigateToTracker = (tracker) => {
        const routes = {
            'symptom': '/symptoms',
            'sleep': '/sleep',
            'food': '/food',
            'flare': '/flares',
            'cycle': '/cycle',
            'supplement': '/supplements'
        };
        const route = routes[tracker.toLowerCase()] || '/dashboard';
        navigate(route);
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                    <Mic className="w-8 h-8 text-indigo-500" />
                    Voice Journal
                </h1>
                <p className="text-muted-foreground mt-1">Speak your symptoms and feelings, AI will transcribe and categorize</p>
            </div>

            {/* Recording Card */}
            <Card className="border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/20">
                <CardContent className="p-6">
                    <div className="flex flex-col items-center gap-6">
                        {/* Recording Button */}
                        <div className="relative">
                            {isRecording && (
                                <div className="absolute inset-0 animate-ping rounded-full bg-red-400 opacity-30" style={{ animationDuration: '1.5s' }} />
                            )}
                            <button
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={transcribing || analyzing}
                                className={`
                                    relative w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-lg
                                    ${isRecording 
                                        ? 'bg-red-500 hover:bg-red-600 shadow-red-500/30' 
                                        : 'bg-gradient-to-br from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 shadow-indigo-500/30'
                                    }
                                `}
                                data-testid="record-button"
                            >
                                {isRecording ? (
                                    <Square className="w-10 h-10 text-white" />
                                ) : (
                                    <Mic className="w-10 h-10 text-white" />
                                )}
                            </button>
                        </div>

                        {/* Recording Status */}
                        <div className="text-center">
                            {isRecording ? (
                                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                    <span className="font-medium">Recording... {formatTime(recordingTime)}</span>
                                </div>
                            ) : audioBlob ? (
                                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                                    <Clock className="w-4 h-4" />
                                    <span>Recording ready ({formatTime(recordingTime)})</span>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">Tap to start recording</p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        {audioBlob && !transcription && (
                            <div className="flex gap-3">
                                <Button
                                    onClick={transcribeAudio}
                                    disabled={transcribing}
                                    className="gap-2"
                                    data-testid="transcribe-button"
                                >
                                    {transcribing ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Wand2 className="w-4 h-4" />
                                    )}
                                    {transcribing ? 'Transcribing...' : 'Transcribe'}
                                </Button>
                                <Button variant="outline" onClick={resetRecording}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Re-record
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Transcription & Analysis */}
            {transcription && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                            Transcription
                        </CardTitle>
                        <CardDescription>Edit if needed, then analyze with AI</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Textarea
                            value={editedTranscription}
                            onChange={(e) => setEditedTranscription(e.target.value)}
                            rows={4}
                            placeholder="Your transcribed text..."
                            data-testid="transcription-textarea"
                        />
                        
                        <div className="flex gap-3">
                            <Button
                                onClick={analyzeTranscription}
                                disabled={analyzing || !editedTranscription}
                                className="gap-2"
                                data-testid="analyze-button"
                            >
                                {analyzing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                            </Button>
                            <Button variant="outline" onClick={resetRecording}>
                                Start Over
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Analysis Results */}
            {analysis && (
                <Card className="border-emerald-200 dark:border-emerald-800">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Activity className="w-5 h-5 text-emerald-500" />
                            AI Analysis
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Summary */}
                        {analysis.summary && (
                            <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30">
                                <p className="text-sm">{analysis.summary}</p>
                            </div>
                        )}

                        <div className="grid md:grid-cols-3 gap-4">
                            {/* Symptoms */}
                            <div className="space-y-2">
                                <p className="font-medium flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-rose-500" />
                                    Symptoms Detected
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {analysis.symptoms?.length > 0 ? (
                                        analysis.symptoms.map((sym, idx) => (
                                            <Badge key={idx} variant="outline" className="border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300">
                                                {sym}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">None detected</span>
                                    )}
                                </div>
                            </div>

                            {/* Feelings */}
                            <div className="space-y-2">
                                <p className="font-medium flex items-center gap-2">
                                    <Heart className="w-4 h-4 text-pink-500" />
                                    Feelings
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {analysis.feelings?.length > 0 ? (
                                        analysis.feelings.map((feeling, idx) => (
                                            <Badge key={idx} variant="outline" className="border-pink-300 text-pink-700 dark:border-pink-700 dark:text-pink-300">
                                                {feeling}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">None detected</span>
                                    )}
                                </div>
                            </div>

                            {/* Triggers */}
                            <div className="space-y-2">
                                <p className="font-medium flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    Potential Triggers
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {analysis.triggers?.length > 0 ? (
                                        analysis.triggers.map((trigger, idx) => (
                                            <Badge key={idx} variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300">
                                                {trigger}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">None detected</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Severity */}
                        {analysis.severity && (
                            <div className="flex items-center gap-3">
                                <span className="font-medium">Overall Severity:</span>
                                <Badge className={
                                    analysis.severity >= 7 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                    analysis.severity >= 4 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                }>
                                    {analysis.severity}/10
                                </Badge>
                            </div>
                        )}

                        {/* Suggested Trackers */}
                        {analysis.suggested_trackers?.length > 0 && (
                            <div className="space-y-2">
                                <p className="font-medium">Suggested Trackers to Update:</p>
                                <div className="flex flex-wrap gap-2">
                                    {analysis.suggested_trackers.map((tracker, idx) => (
                                        <Button
                                            key={idx}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigateToTracker(tracker)}
                                            className="gap-1"
                                        >
                                            {tracker}
                                            <ChevronRight className="w-3 h-3" />
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Save Button */}
                        <div className="pt-4 border-t">
                            <Button
                                onClick={saveEntry}
                                disabled={saving}
                                className="gap-2"
                                data-testid="save-entry-button"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                {saving ? 'Saving...' : 'Save Journal Entry'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Previous Entries */}
            <div className="space-y-4">
                <h2 className="font-heading font-semibold text-xl">Previous Entries</h2>
                
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                    </div>
                ) : entries.length === 0 ? (
                    <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                            <Mic className="w-12 h-12 text-muted-foreground/50 mb-4" />
                            <h3 className="font-medium text-lg mb-2">No journal entries yet</h3>
                            <p className="text-muted-foreground text-center max-w-md">
                                Record your first voice journal entry above to get started!
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {entries.map((entry) => (
                            <Card key={entry.id} data-testid={`entry-${entry.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 space-y-3">
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Clock className="w-4 h-4" />
                                                {format(parseISO(entry.created_at), 'PPpp')}
                                                {entry.audio_duration && (
                                                    <Badge variant="outline" className="ml-2">
                                                        {formatTime(entry.audio_duration)}
                                                    </Badge>
                                                )}
                                            </div>
                                            
                                            {entry.summary && (
                                                <p className="text-sm">{entry.summary}</p>
                                            )}
                                            
                                            <div className="flex flex-wrap gap-2">
                                                {entry.symptoms?.slice(0, 3).map((sym, idx) => (
                                                    <Badge key={idx} variant="outline" className="text-xs border-rose-200 text-rose-600">
                                                        {sym}
                                                    </Badge>
                                                ))}
                                                {entry.feelings?.slice(0, 2).map((feeling, idx) => (
                                                    <Badge key={idx} variant="outline" className="text-xs border-pink-200 text-pink-600">
                                                        {feeling}
                                                    </Badge>
                                                ))}
                                                {entry.triggers?.slice(0, 2).map((trigger, idx) => (
                                                    <Badge key={idx} variant="outline" className="text-xs border-amber-200 text-amber-600">
                                                        {trigger}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => deleteEntry(entry.id)}
                                            className="text-muted-foreground hover:text-destructive"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
